import { ApprovedIcon } from '@/assets/icons/info-icons/approved';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import Input from '@/components/basicComponents/input';
import Label from '@/components/basicComponents/label';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import RadioButton from '@/components/basicComponents/radioButton';
import Select, { ISelectValue } from '@/components/basicComponents/select';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import { GET_CURRENT_MANAGERS_QUERY, UPDATE_INVESTMENT_MUTATION } from '@/components/dueDiligence-page/queries';
import { useErrorMessages } from '@/hooks/i18n';
import { themeVar } from '@/styles/theme';
import { useMutation, useQuery } from '@apollo/client';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { EDIT_STRATEGY_PAGE_INVESTMENT_QUERY } from '../queries';
import { sortUniqueNoneEmpty } from '../utils';

enum AssignManagerOptionEnum {
  CreateNewManager = 'Create a new manager',
  AssignToExisting = 'Assign to existing'
}

interface IForm {
  managerOption: string;
  newManagerName: string;
  existingManager: string;
}

const defaultFormValues: IForm = {
  managerOption: AssignManagerOptionEnum.CreateNewManager,
  newManagerName: '',
  existingManager: ''
};

enum DialogStatus {
  SUCCESS = 'success',
  WARNING = 'warning'
}

interface AssignManagerProps {
  isOpen: boolean;
  strategy: {
    id: string;
    name: string;
    manager: string;
  };
  strategiesSelectOptions: ISelectValue[];
  onClose: () => void;
}

const AssignManager = ({ isOpen, strategy, strategiesSelectOptions, onClose }: AssignManagerProps) => {
  const { default: defaultErrorMessage } = useErrorMessages();
  const { control, handleSubmit, watch } = useForm<IForm>({ defaultValues: defaultFormValues });

  const [assignManagerRadioButtonOptions, setAssignManagerRadioButtonOptions] = useState([]);
  const [existingManagerSelectOptions, setExistingManagerSelectOptions] = useState<ISelectValue[]>([]);
  const [prevManager, setPrevManager] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [modalWindowType, setModalWindowType] = useState<DialogStatus | null>(null);

  const [updateInvestment, { loading: updateInvestmentLoading }] = useMutation(UPDATE_INVESTMENT_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries: [EDIT_STRATEGY_PAGE_INVESTMENT_QUERY]
  });

  useEffect(() => {
    if (strategy?.manager) {
      setPrevManager(strategy.manager);
      setAssignManagerRadioButtonOptions(Object.values(AssignManagerOptionEnum));
      return;
    }
    setAssignManagerRadioButtonOptions([AssignManagerOptionEnum.CreateNewManager, AssignManagerOptionEnum.AssignToExisting]);
  }, [strategy?.manager]);

  const { loading: existingManagersLoading } = useQuery(GET_CURRENT_MANAGERS_QUERY, {
    onCompleted: (data) => {
      const sortedManagerList = sortUniqueNoneEmpty([...data.getCurrentManagers.values]?.map((manager) => createSimpleSelectOption(manager)));
      setExistingManagerSelectOptions(sortedManagerList);
    },
    onError: (error) => {
      console.error(error);
    }
  });

  const handleAssignManager = async (data: IForm) => {
    if (strategiesSelectOptions.length === 1) {
      setModalWindowType(DialogStatus.WARNING);
      return;
    }

    await saveValue(data);
  };

  const saveValue = async (data: IForm) => {
    try {
      setErrorMessage(null);
      setModalWindowType(null);

      const { managerOption, newManagerName, existingManager } = data;
      let managerId = '';
      if (managerOption === AssignManagerOptionEnum.CreateNewManager) {
        managerId = newManagerName;
      } else if (managerOption === AssignManagerOptionEnum.AssignToExisting) {
        managerId = existingManager;
      }

      await updateInvestment({
        variables: {
          data: {
            id: strategy.id,
            managerId
          }
        }
      });

      setModalWindowType(DialogStatus.SUCCESS);
    } catch (error) {
      console.error(error);
      setErrorMessage(defaultErrorMessage);
    }
  };

  const getSuccessDialogDescription = () => {
    const managerOption = watch('managerOption');
    const newManagerName = watch('newManagerName');
    const existingManager = watch('existingManager');

    if (managerOption === AssignManagerOptionEnum.CreateNewManager) {
      return (
        <>
          <b>{strategy.name}</b> has been added to <b>{newManagerName}</b>.
        </>
      );
    }
    if (managerOption === AssignManagerOptionEnum.AssignToExisting) {
      return (
        <>
          <b>{strategy.name}</b> has been assigned to <b>{existingManager}</b>.
        </>
      );
    }
  };

  if (modalWindowType === DialogStatus.WARNING) {
    return (
      <ModalWrappedContent
        isOpen={isOpen}
        onClose={onClose}
        title="Delete Manager"
        confirmButton={{ name: 'Confirm', onClick: handleSubmit(saveValue) }}
      >
        <ModalBody>
          <DeleteManagerDescription>
            <b>{strategiesSelectOptions?.[0]?.label || ''}</b> is the only strategy assigned to this manager. After this action is completed, this
            manager will be deleted automatically.
          </DeleteManagerDescription>
          <DeleteManagerDescription>Please confirm that you want to proceed.</DeleteManagerDescription>
        </ModalBody>
      </ModalWrappedContent>
    );
  }

  if (modalWindowType === DialogStatus.SUCCESS) {
    return (
      <ModalWrappedContent isOpen={isOpen} onClose={onClose} confirmButton={{ name: 'OK', onClick: onClose }} showCancelButton={false}>
        <ModalBody>
          <ApprovedIcon width={60} height={60} />
          <ModalTitle>Success</ModalTitle>
          <ModalDescription>{getSuccessDialogDescription()}</ModalDescription>
        </ModalBody>
      </ModalWrappedContent>
    );
  }

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      title={strategy?.manager ? 'Change Manager' : 'Assign Manager'}
      confirmButton={{
        name: 'Confirm',
        onClick: handleSubmit(handleAssignManager),
        disabled: updateInvestmentLoading
      }}
      showCancelButton={!updateInvestmentLoading}
      showCloseIcon={!updateInvestmentLoading}
      isUseOutsideClick={!updateInvestmentLoading}
    >
      {updateInvestmentLoading ? (
        <ModalLoader />
      ) : (
        <>
          <ModalDescription>
            Create a new manager for <b>{strategy.name}</b> or select an existing one.
          </ModalDescription>
          {errorMessage && (
            <ErrorBannerWrapper>
              <Banner
                icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
                title="Error"
                description={errorMessage}
                bgColor={themeVar('context.error')}
              />
            </ErrorBannerWrapper>
          )}
          <AssignManagerFieldsWrapper>
            <div>
              <Label size="md">Manager</Label>
              <Controller
                name="managerOption"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <OptionsWrapper>
                    {assignManagerRadioButtonOptions.map((option, index) => (
                      <RadioButton
                        key={option + index}
                        name="managerRadioButton"
                        radioButtonText={option}
                        selectedOption={value}
                        handleRadioChange={(event) => onChange(event.target.value)}
                      />
                    ))}
                  </OptionsWrapper>
                )}
              />
            </div>
            <AnimatePresence mode="wait">
              {watch('managerOption') === AssignManagerOptionEnum.CreateNewManager && (
                <motion.div key="newManagerNameInput" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <Controller
                    name="newManagerName"
                    control={control}
                    rules={{
                      required: {
                        value: true,
                        message: 'Manager Name is required'
                      }
                    }}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <Input value={value || ''} onChange={onChange} errorText={error?.message} label="Manager Name" size="md" isRequiredField />
                    )}
                  />
                </motion.div>
              )}
              {watch('managerOption') === AssignManagerOptionEnum.AssignToExisting && (
                <motion.div key="existingManagerSelect" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <Controller
                    name="existingManager"
                    control={control}
                    rules={{
                      required: {
                        value: true,
                        message: 'Select Manager is required'
                      }
                    }}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <Select
                        value={value}
                        setValue={onChange}
                        options={existingManagerSelectOptions}
                        errorText={error?.message}
                        label="Select Manager"
                        size="md"
                        loading={existingManagersLoading}
                        withSearch
                        isRequiredField
                      />
                    )}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </AssignManagerFieldsWrapper>
        </>
      )}
    </ModalWrappedContent>
  );
};

export default AssignManager;

const ModalBody = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
`;

const ModalTitle = styled.div`
  font-family: Blinker;
  font-size: 33px;
  font-style: normal;
  font-weight: 400;
  line-height: 46.2px;
  text-align: center;
  color: ${themeVar('font.strong')};
`;

const ModalDescription = styled.div`
  font-size: 16px;
  line-height: 22.4px;
  text-align: center;
  padding-bottom: 35px;
  color: ${({ theme }) => theme.font.base};
`;

const DeleteManagerDescription = styled.div`
  font-size: 19px;
  line-height: 26.6px;
  text-align: center;
  color: ${({ theme }) => theme.font.base};
`;

const AssignManagerFieldsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const OptionsWrapper = styled.div`
  width: 100%;
  font-size: 16px;
  font-weight: 400;
  line-height: 22.4px;
`;

const ErrorBannerWrapper = styled.div`
  margin-bottom: 10px;
`;
